import React, { useEffect, useState } from 'react'
import { Badge } from 'antd'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import Select from 'react-select'
import Creatable from 'react-select/creatable'

import { permissions, usePet, useReports, useSocketEvents, useLatestPathname } from '../../modules'

import Dashboard from './Dashboard'
import Inventory from './Inventory'
import StoreRequests from './StoreRequests'
import PatientRequests from './PatientRequests'
import DepartmentRequests from './DepartmentRequests'
import ProcedureConsumables from './ProcedureConsumables'

import Confirm from '../../components/reusable/confirm'
import Routing, { MenuItem, SideMenu } from '../../components/reusable/routing'
import CriticalItems from './CriticalItems'
import ExpiringSoon from './ExpiringSoon'
import StockTakes from './StockTakes'
import Returns from './Returns'

let menu = []

const Store = () => {
    const history = useHistory()
    const { user, lastInventoryAlert } = useSelector(state => state.application)
    const pathname = useLatestPathname()
    
    const [storeName, setStoreName] = useState('')
    const [store, setStore] = useState()
    const [critical, setCritical] = useState(0)
    const [nearExpiry, setNearExpiry] = useState(0)
    const [returns, setReturns] = useState(0)
    const [stores, setStores] = useState([])

    const [Reports, reports] = useReports("store")
    
    const [, getAllowed] = usePet('myAllowedStores', {
        name: storeName
    }, { notify: true, callback: ({ data }) => {
        let allowed = data?.map(st => ({
            ...st,
            _id: st?.store?._id,
            label: st?.store?.name,
            value: st?.store?._id,
            permissions: st?.permissions
        }))

        if (allowed?.length > 0) {
            setStores(allowed)
            setStore(allowed[0])
        }
    } })
    
    const [, newStore] = usePet('newStore', {
        name: storeName
    }, { notify: true, callback: getAllowed, manual: true, load: { msg: 'Adding store...' } })

    const [, getCritical] = usePet('criticalItems', {
        store: store?._id,
    }, { notify: true, manual: true, callback: ({ data }) => {
        setCritical(data.count)
    } })

    const [, getNearExpiry] = usePet('nearExpiryItems', {
        store: store?._id,
    }, { notify: true, manual: true, callback: ({ data }) => {
        setNearExpiry(data.count)
    } })

    const [, pendingReturns] = usePet('pendingReturns', {
        store: store?._id,
    }, { notify: true, manual: true, callback: ({ data }) => {
        setReturns(data)
    } })

    useEffect(() => { 
        if (store) {
            getCritical()
            getNearExpiry()

            menu = [
                {
                    path: 'dashboard',
                    title: props =>
                        <Badge>
                            <MenuItem>Dashboard</MenuItem>
                        </Badge>,
                    component: ({ path }) => <Dashboard stores={ stores } setStore={ setStore } />
                },
                {
                    path: 'inventory',
                    title: props => 
                        <Badge>
                            <MenuItem>Inventory</MenuItem>
                        </Badge>,
                    component: ({ path }) => <Inventory store={ store } />
                },
                {
                    path: 'critical-items',
                    title: props => 
                        <Badge count={ critical }>
                            <MenuItem>Critical Items</MenuItem>
                        </Badge>,
                    component: ({ path }) => <CriticalItems store={ store } />
                },
                {
                    path: 'expiring-soon',
                    title: props =>
                        <Badge count={ nearExpiry }>
                            <MenuItem>Expiring Soon</MenuItem>
                        </Badge>,
                    component: ({ path }) => <ExpiringSoon store={ store } />
                },
                {
                    path: 'returns',
                    title: props => 
                        <Badge count={ returns }>
                            <MenuItem>Returns</MenuItem>
                        </Badge>,
                    component: ({ path }) => <Returns store={ store } />
                },
                {
                    path: 'store-requests',
                    title: props => 
                        <Badge>
                            <MenuItem>Store Requests</MenuItem>
                        </Badge>,
                    component: ({ path }) => <StoreRequests store={ store } />
                },
                {
                    path: 'procedure-consumables',
                    title: props => 
                        <Badge>
                            <MenuItem>Procedure Consumables</MenuItem>
                        </Badge>,
                    component: ({ path }) => <ProcedureConsumables store={ store } />
                },
                {
                    path: 'department-requests',
                    title: props => 
                        <Badge>
                            <MenuItem>Department Requests</MenuItem>
                        </Badge>,
                    component: ({ path }) => <DepartmentRequests store={ store } />
                },
                {
                    path: 'inpatient-requests',
                    title: props => 
                        <Badge>
                            <MenuItem>Inpatient Requests</MenuItem>
                        </Badge>,
                    component: ({ path }) => <PatientRequests store={ store } />
                },
                {
                    path: 'stock-take',
                    title: props => 
                        <Badge>
                            <MenuItem>Stock Take</MenuItem>
                        </Badge>,
                    component: ({ path }) => <StockTakes store={ store } />
                },
                {
                    path: 'reports',
                    title: 'Reports',
                    component: ({ path }) => <Reports reports={ reports } department="store" additional={{ store: store?._id }} />
                },
            ]
            
            if (!pathname.current.startsWith("/app/store/dashboard")) history.push("/app/store/dashboard")
        }

        return () => null
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store, reports, critical, nearExpiry])

    useEffect(() => {
        if (!store?._id || !lastInventoryAlert) return
        if (lastInventoryAlert.storeId !== store._id && lastInventoryAlert.destination !== store.name) return
        getCritical()
        getNearExpiry()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastInventoryAlert, store?._id, store?.name])

    useSocketEvents({
        'new-return': {
            condition: data => data.store === store?._id,
            notif: {
                message: 'New Return Request',
                type: 'info'
            },
            callback: pendingReturns
        }
    })

    return (
        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto flex flex-no-wrap max-h-full overflow-hidden">
            <div className="w-1/6 overflow-y-scroll">
                <p className="text-2xl font-semibold">Store</p>
                {
                    permissions(user, 'store', 'create-stores') || permissions(user, 'admin', 'stores') ?
                        <Creatable
                            value={store}
                            onChange={setStore}
                            onCreateOption={val => {
                                setStoreName(val)
                                Confirm({
                                    title: 'Create New Store?',
                                    message: `create new store called ${val}?`,
                                    okText: 'Proceed',
                                    onOk: newStore
                                })
                            }}
                            className="mb-4"
                            styles={{ input: styles => ({ ...styles, minWidth: '10em' }) }}
                            options={ stores.map(st => ({ label: st?.store?.name, value: st?.store?._id, _id:  st?.store?._id })) }
                            placeholder="Select Or Add New Store" />
                        :
                        <Select
                            value={store}
                            onChange={setStore}
                            className="mb-4"
                            styles={{ input: styles => ({ ...styles, minWidth: '10em' }) }}
                            options={ stores.map(st => ({ label: st?.store?.name, value: st?.store?._id, _id:  st?.store?._id })) }
                            placeholder="Select Store" />
                }
                <SideMenu namespace="store" menu={ menu } />
            </div>
            <div className="w-5/6 overflow-y-scroll">
                <Routing namespace="store" menu={ menu } />
            </div>
        </div>
    )
}

export default Store
